import React, { useRef } from "react";
import {
  CreationFormInput,
  CreationFormTemplate,
} from "../../components/form/CreationForm";
import { Recipe } from "./Recipe";
import { TabbedContent } from "../../components/tabs/TabbedContent";
import { Ingredient, SpecificIngredient } from "../ingredients/Ingredient";
import { AddIngredientGroupFormTemplate } from "../ingredients/FormTemplate";
import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { InputTypes } from "../InputTypes";
import { IngredientGroup } from "./IngredientGroup";
import { useAliments } from "../../hooks";
import { OptionsSources } from "../OptionsSources";
import { ScrollableDragDropForm } from "../../components/form/DragDropForm";
export const IngredientForm = forwardRef((props, ref) => {
  //Legend for the ingredient units, as they need to be modified
  const [query] = useState(0);
  const { aliments } = useAliments(query);

  //prep_var value
  const [prepVar, setPrepVar] = useState("");

  //generated name and preparation from testRecipe
  const [generatedName, setGeneratedName] = useState("");
  const [generatedPreparation, setGeneratedPreparation] = useState("");

  //List that only contains _id, home_measure, home_quantity and net_weight of aliments
  const [measures, setMeasures] = useState([]);

  //Names of the tabs
  const ingredientTabs = [
    Recipe.veg_ingredients,
    Recipe.carb_ingredients,
    Recipe.pro_ingredients,
    Recipe.fat_ingredients,
    Recipe.dairy_ingredients,
    Recipe.fruit_ingredients,
    Recipe.seasoning_ingredients,
  ];

  //Codes of the ingredient groups
  const ingredientGroups = [
    "VEGE",
    "CARB",
    "PROT",
    "FATS",
    "DAIR",
    "FRUT",
    "SEAS",
  ];

  const homeMeasures = {
    SMAB: "Bol Pequeño",
    SMAC: "Lata Pequeña",
    SMDD: "Plato Pequeño de Postre",
    SMAD: "Plato Pequeño",
    SMMD: "Plato Pequeño en Cocido",
    BIMD: "Plato Grande",
    MEMD: "Plato Mediano",
    GARR: "Guarnición",
    GLAS: "Vaso",
    HAND: "Puñado",
    TABL: "Cucharada Sopera",
    TEAS: "Cucharadita de Café",
    UNIT: "Unidad",
    BIGU: "Unidad Grande",
    MEDU: "Unidad Mediana",
    PORT: "Porción",
    SLIC: "Rebanada",
    CMS5: "Rebanada de 5cm", //5CMS on back, but can't be named like that in JS
    TERR: "Tarrina",
    CLOV: "Diente",
    PINC: "Pizca",
    SPLA: "Chorrito ",
  };

  //Template for objects in form
  const formObjectTemplate = getCopy(Ingredient);

  //List with the ingredient data
  const [ingredientList, setIngredientList] = useState(initFormData());

  //Current tab selected
  const [currentTab, setCurrentTab] = useState(0);

  //Array with the visible name in each group, in order to put them on the drag and drops
  const [visibleNames, setVisibleNames] = useState(
    ingredientGroups.reduce((acc, group) => {
      acc[group] = "";
      return acc;
    }, {})
  );

  //Array with all the preparations from each group
  const [groupPreparations, setGroupPreparations] = useState(
    ingredientGroups.reduce((acc, group) => {
      acc[group] = "";
      return acc;
    }, {})
  );

  //ref for using the add/delete buttons
  const ingredientSetRef = useRef();

  useImperativeHandle(ref, () => ({
    getIngredientList: () => {
      return ingredientList;
    },
  }));

  useEffect(() => {
    if (aliments) {
      const newMeasures = aliments.map((alim) => ({
        _id: alim._id,
        name: alim.food_name.nameES,
        home_measure: alim.home_measure,
        home_quantity: alim.home_quantity,
        net_weight: alim.net_weight,
      }));

      setMeasures(newMeasures);
    }
  }, [aliments]);

  useEffect(() => {
    setPrepVar(props.prepVar);
    setGeneratedName(props.generatedName);
    setGeneratedPreparation(props.generatedPreparation);
  }, [props]);

  function getCopy(objectToCopy) {
    let copy = {};
    Object.keys(objectToCopy).forEach((key) => {
      copy[key] = undefined;
    });
    return copy;
  }

  function initFormData() {
    let formData = {};
    ingredientGroups.forEach((ingredientGroup) => {
      formData[ingredientGroup] = Array(1).fill(formObjectTemplate);
    });
    return formData;
  }

  function changeUnitsForBack(json) {
    if (json["units"] === "g" || json["units"] === "ml") {
      //No change needed
      return {
        max_amount: json["max_amount"],
        min_amount: json["min_amount"],
        units: json["units"],
      };
    }
    //A home_measure was selected, we change it
    let aliment = measures.find((alim) => alim._id === json["_id"]);

    let max_amount =
      (aliment.net_weight / aliment.home_quantity) * json["max_amount"];
    let min_amount =
      (aliment.net_weight / aliment.home_quantity) * json["min_amount"];
    let units = json["units"] === "GLAS" ? "ml" : "g";

    return {
      max_amount: max_amount,
      min_amount: min_amount,
      units: units,
    };
  }

  //Parses an ingredient form to JSON format
  function ingredientFormToJson(form) {
    const formData = new FormData(form);
    const json = {};

    formData.forEach((value, key) => {
      json[key] = value;
    });

    //(TEXT_AREA) Deleted \n to save plain text only
    json["ingredient_preparation"] = json["ingredient_preparation"].replace(
      /\n/g,
      " "
    );

    return json;
  }

  //To control the data input on numbers
  function handleChange(ev, formId) {
    if (ev.target !== undefined && ev.target.type === "number") {
      //this instead of isNaN(), because of the personalized forms
      if (ev.target.value === "") {
        ev.target.value = 0;
      }
    }
  }

  function handleTabChange() {
    //search the active tab
    var active = document.getElementsByClassName("active")[2];

    //get the index from the tab
    var index = ingredientTabs.findIndex(function (element) {
      return element.visibleName === active.textContent;
    });

    setCurrentTab(index);
  }

  function handleBlurInAVisibleName(ev) {
    setVisibleNames((oldNames) => {
      var newNames = { ...oldNames };
      newNames[ingredientGroups[currentTab]] = ev.target.value;
      return newNames;
    });
  }

  function handleBlurInAGroupPreparation(ev) {
    setGroupPreparations((oldPreparations) => {
      var newPreparations = { ...oldPreparations };
      newPreparations[ingredientGroups[currentTab]] = ev.target.value.replace(
        /\n/g,
        " "
      );
      return newPreparations;
    });
  }

  //Saves the data from the form to the ingredient list
  function saveIngredient(ev) {
    ev.preventDefault();
    const form = ev.currentTarget.form;
    const json = ingredientFormToJson(form);

    //If no ingredient is selected
    if (json["_id"] === "") {
      return;
    }

    //we change the units back to g/ml, as they're the only values possible on backend
    var changedUnits = changeUnitsForBack(json);
    json["units"] = changedUnits["units"];
    json["max_amount"] = changedUnits["max_amount"];
    json["min_amount"] = changedUnits["min_amount"];

    setIngredientList((prevList) => {
      var newList = { ...prevList };

      //If there's no ingredients in that ingredient group, as the lists start with an empty object
      if (newList[ingredientGroups[currentTab]][0]._id === undefined) {
        newList[ingredientGroups[currentTab]][0] = json;
      }
      //There's at least one ingredient on this group already
      else {
        var listWithoutNewIng = newList[ingredientGroups[currentTab]].filter(
          (ing) => ing._id !== json._id
        );

        if (
          listWithoutNewIng.length ===
          newList[ingredientGroups[currentTab]].length
        ) {
          // We add the new ing
          newList[ingredientGroups[currentTab]].push(json);
        } else {
          // We modify the ing with same _id
          newList[ingredientGroups[currentTab]] = listWithoutNewIng;
          newList[ingredientGroups[currentTab]].push(json);
        }
      }
      console.log(newList);
      return newList;
    });
  }

  //Removes the ingredient of the list, if one was submitted
  function removeIngredientForm(id) {
    setIngredientList((prevList) => {
      let newList = { ...prevList };
      newList[ingredientGroups[currentTab]] = newList[
        ingredientGroups[currentTab]
      ].filter((ing) => ing._id !== id);

      //if array is empty, add the template
      if (newList[ingredientGroups[currentTab]].length === 0) {
        newList[ingredientGroups[currentTab]].push(formObjectTemplate);
      }

      console.log(newList);
      return newList;
    });
  }

  //One form per tab is initialized at the start
  return (
    <div className={"pl-10 pr-10"}>
      <CheckRecipeSection
        ingredients={ingredientList}
        ingredientGroups={ingredientGroups}
        measures={measures}
        visibleNames={visibleNames}
        prepVar={prepVar}
        generatedName={generatedName}
        generatedPreparation={generatedPreparation}
      />

      <div className="container-group col-sm-12" style={{ marginLeft: "-1em" }}>
        <h3 className="m-t-10 m-b-0 ">{"Ingredientes"}</h3>
        <hr className="m-t-10" />
      </div>
      <TabbedContent
        removeContent={false}
        onTabChange={handleTabChange}
        tabHeaders={ingredientTabs.map((tab, index) => {
          return (
            <button data-toggle="tab" aria-expanded={index === 0}>
              {tab.visibleName}
            </button>
          );
        })}
      >
        {ingredientGroups.map((ingredientGroup) => {
          return (
            <>
              {/** Form for the details of ingredient group */}
              <CreationFormTemplate
                defaultData={formObjectTemplate}
                onChange={(ev) =>
                  handleChange(ev, ingredientGroups[currentTab] + "-" + 0)
                }
                formTemplate={AddIngredientGroupFormTemplate(
                  ingredientGroups[currentTab],
                  handleBlurInAVisibleName,
                  handleBlurInAGroupPreparation
                )}
                dataTemplate={IngredientGroup}
              />
              <IngredientSet
                ref={ingredientSetRef}
                currentGroup={ingredientGroup}
                formObjectTemplate={formObjectTemplate}
                saveIngredient={saveIngredient}
                removeIngredientForm={removeIngredientForm}
                measures={measures}
                homeMeasures={homeMeasures}
                ingredientList={ingredientList}
                currentTab={currentTab}
                groupPreparations={groupPreparations}
              />
            </>
          );
        })}
      </TabbedContent>
      <div className="container-group col-sm-12" style={{ marginLeft: "-1em" }}>
        <h3 className="m-t-10 m-b-0 ">{"Detalles de la receta"}</h3>
        <hr className="m-t-10" />
      </div>
    </div>
  );
});

export const IngredientSet = React.forwardRef((props, ref) => {
  const {
    currentGroup,
    formObjectTemplate,
    saveIngredient,
    removeIngredientForm,
    measures,
    homeMeasures,
    ingredientList, //For ingredient groups
    currentTab, //For ingredient groups
    groupPreparations, //For preparation buttons
  } = props;

  const [groupPreparationsList, setGroupPreparationsList] = useState([]);

  useEffect(() => {
    setGroupPreparationsList(groupPreparations);
  }, [groupPreparations]);

  const ingredientListRef = useRef();

  const ingPrepButtonsRef = useRef();

  function defaultUnitsInputData() {
    let defaultUnits = ["g", "ml"];
    let unitsData = defaultUnits.map((unit) => {
      return { value: unit, label: unit };
    });
    return unitsData;
  }

  const [unitOptions, setUnitOptions] = useState(defaultUnitsInputData());
  const [selectedUnit, setSelectedUnit] = useState(0);

  const [inputData, setInputData] = useState(
    SpecificIngredient(currentGroup)._id
  );

  //Values for the form, they change as the saved ingredient is pressed
  const [minAmount, setMinAmount] = useState(0);
  const [maxAmount, setMaxAmount] = useState(0);
  const [weight, setWeight] = useState(0);
  const [isVariable, setIsVariable] = useState();
  const [isVisible, setIsVisible] = useState();
  const [preparation, setPreparation] = useState("");

  const [ingredientSelect, setIngredientSelect] = useState();

  function handleIngredientChange(val) {
    setIngredientSelect(val);
    setUnitOptions((prevUnitOptions) => {
      let newUnitOptions = [...prevUnitOptions];
      newUnitOptions[2] = homeMeasure(val.value);

      setSelectedUnit(newUnitOptions[0]);
      return newUnitOptions;
    });
  }

  function homeMeasure(id) {
    let aliment = measures.find((alim) => alim._id === id);
    if (aliment.home_measure === "5CMS") {
      return {
        value: aliment.home_measure,
        label:
          homeMeasures["CMS5"] +
          " (" +
          aliment.home_quantity * aliment.net_weight +
          " g)",
      };
    }
    if (aliment.home_measure === "GLAS") {
      return {
        value: aliment.home_measure,
        label:
          homeMeasures[aliment.home_measure] +
          " (" +
          aliment.home_quantity * aliment.net_weight +
          " ml)",
      };
    }

    if (aliment.home_measure === "PINC" || aliment.home_measure === "SPLA") {
      return {
        value: aliment.home_measure,
        label: homeMeasures[aliment.home_measure],
      };
    }

    //rest of the aliments
    return {
      value: aliment.home_measure,
      label:
        homeMeasures[aliment.home_measure] +
        " (" +
        aliment.home_quantity * aliment.net_weight +
        " g)",
    };
  }

  function handleUnitChange(val) {
    setSelectedUnit(val);
  }

  function handleSave(ev) {
    saveIngredient(ev);
    ingredientListRef.current.addToList(ev);
    setInputData(SpecificIngredient(currentGroup)._id);
    resetForm();
  }

  function resetForm() {
    setMinAmount(0);
    setMaxAmount(0);
    setWeight(0);
    setIsVariable({
      value: "true",
      label: "Sí",
    });
    setIsVisible({
      value: "true",
      label: "Sí",
    });
    setPreparation("");

    setIngredientSelect("");
    setSelectedUnit(0);
    setUnitOptions(defaultUnitsInputData());
  }

  function changeIngredientForm(json) {
    let alimentName = measures.find((alim) => alim._id === json._id).name;
    setIngredientSelect({
      value: json._id,
      label: alimentName,
    });

    setUnitOptions((prevUnitOptions) => {
      let newUnitOptions = [...prevUnitOptions];
      newUnitOptions[2] = homeMeasure(json._id);
      let optionIndex;
      switch (json.units) {
        case "g":
          optionIndex = 0;
          break;
        case "ml":
          optionIndex = 1;
          break;
        default:
          optionIndex = 2;
      }

      setSelectedUnit(newUnitOptions[optionIndex]);
      return newUnitOptions;
    });

    setMinAmount(json.min_amount);
    setMaxAmount(json.max_amount);
    setWeight(json.priority_weight);
    setIsVariable({
      value: json.is_variable === "true",
      label: json.is_variable === "true" ? "Sí" : "No",
    });
    setIsVisible({
      value: json.is_visible === "true",
      label: json.is_visible === "true" ? "Sí" : "No",
    });
    setPreparation(json.ingredient_preparation);
  }

  function handleMinAmountChange(ev) {
    if (ev.target.value === "") {
      ev.target.value = 0;
    }
    setMinAmount(ev.target.value);
  }
  function handleMaxAmountChange(ev) {
    if (ev.target.value === "") {
      ev.target.value = 0;
    }
    setMaxAmount(ev.target.value);
  }
  function handleWeightChange(ev) {
    if (ev.target.value === "") {
      ev.target.value = 0;
    }
    setWeight(ev.target.value);
  }

  function removeIngredient() {
    if (ingredientSelect !== undefined) {
      let id = ingredientSelect.value;
      removeIngredientForm(id);
      ingredientListRef.current.removeIngredientOfList(id);
    }

    resetForm();
  }

  function handleIngredientPreparationButton(id) {
    var ingredientWithId = ingredientList[currentGroup].find(
      (ingredient) => ingredient._id === id
    );

    //If there's no ingredient with that id
    //is because the function is called from the
    //group preparation button
    var repeatedPreparation = ingredientWithId
      ? ingredientWithId.ingredient_preparation
      : groupPreparations[currentGroup];

    setPreparation(repeatedPreparation);
  }

  return (
    <form id={currentGroup + "-ingredientForm"}>
      <div key={currentGroup} className="fade in mt-20">
        <div key={currentGroup}>
          <span
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <CreationFormInput
              id={"ingSelect-" + currentGroup}
              name={"_id"}
              size={0.5}
              defaultValue={formObjectTemplate._id}
              inputData={inputData}
              onChange={(val) => handleIngredientChange(val)}
              value={ingredientSelect}
            />
            <span>
              <button
                type="button"
                className="btn btn-purple pull-right mr-10"
                data-toggle="tooltip"
                onClick={(ev) => handleSave(ev)}
              >
                <i className="far fa-save"></i> Guardar
              </button>
              <span
                className="btn btn-danger pull-right mr-10"
                data-toggle="tooltip"
                onClick={() => removeIngredient()}
              >
                <i className="far fa-trash-alt" /> Eliminar
              </span>
            </span>
          </span>

          <CreationFormInput
            id={`unitsSelect-${currentGroup}`}
            name="units"
            size={1 / 3}
            inputData={{
              type: InputTypes.SELECT,
              visibleName: "Unidades",
              optionsMapping: {
                source: OptionsSources.INLINE,
                options: unitOptions,
              },
            }}
            value={selectedUnit}
            onChange={(val) => handleUnitChange(val)}
          />
          <CreationFormInput
            id={""}
            name="min_amount"
            size={1 / 3}
            inputData={{
              visibleName: "Cantidad mínima",
              type: InputTypes.NUMBER,
              min: 0,
              readOnly: currentGroup === "VEGE" || currentGroup === "SEAS",
            }}
            value={minAmount}
            onChange={(ev) => handleMinAmountChange(ev)}
          />
          <CreationFormInput
            id={""}
            name="max_amount"
            size={1 / 3}
            inputData={{
              visibleName: "Cantidad máxima",
              type: InputTypes.NUMBER,
              min: 0,
              readOnly: currentGroup === "SEAS",
            }}
            value={maxAmount}
            onChange={(ev) => handleMaxAmountChange(ev)}
          />
          <CreationFormInput
            id={""}
            name="priority_weight"
            size={1 / 3}
            inputData={{
              visibleName: "Peso de prioridad",
              type: InputTypes.NUMBER,
              min: 0,
            }}
            value={weight}
            onChange={(ev) => handleWeightChange(ev)}
          />
          <CreationFormInput
            id={""}
            name="is_variable"
            size={1 / 3}
            inputData={{
              visibleName: "Variable",
              type: InputTypes.SELECT,
              optionsMapping: {
                source: OptionsSources.BOOLEAN,
              },
            }}
            value={isVariable}
            onChange={(val) => setIsVariable(val)}
          />
          <CreationFormInput
            id={""}
            name="is_visible"
            size={1 / 3}
            inputData={{
              visibleName: "Visible",
              type: InputTypes.SELECT,
              optionsMapping: {
                source: OptionsSources.BOOLEAN,
              },
            }}
            value={isVisible}
            onChange={(val) => setIsVisible(val)}
          />
          <IngredientPreparationButtons
            ref={ingPrepButtonsRef}
            ingredientList={ingredientList}
            currentGroup={currentGroup}
            measures={measures}
            handleIngredientPreparationButton={
              handleIngredientPreparationButton
            }
            groupPreparations={groupPreparationsList}
          />
          <CreationFormInput
            id={""}
            name="ingredient_preparation"
            size={1}
            inputData={{
              visibleName: "Preparación adicional",
              type: InputTypes.TEXT_AREA,
            }}
            value={preparation}
            onChange={(ev) => setPreparation(ev.target.value)}
          />
        </div>
      </div>
      <IngredientList
        ref={ingredientListRef}
        measures={measures}
        changeIngredientForm={changeIngredientForm}
      />
    </form>
  );
});

export const IngredientPreparationButtons = forwardRef((props, ref) => {
  const {
    ingredientList,
    currentGroup,
    measures,
    handleIngredientPreparationButton,
    groupPreparations,
  } = props;

  const [ingredients, setIngredients] = useState([]);

  useEffect(() => {
    setIngredients((oldIngredients) => {
      var filteredIngredients = ingredientList[currentGroup].filter(
        (ingredient) => ingredient._id !== undefined
      );
      return filteredIngredients;
    });
  }, [ingredientList]);

  function handleButtonClick(ev, id) {
    ev.preventDefault();
    handleIngredientPreparationButton(id);
  }

  return (
    <div className="p-5 m-5">
      {(ingredients.length > 0 ||
        (groupPreparations != undefined &&
          groupPreparations[currentGroup] !== "")) && (
        <h5>{"Pulse sobre un ingrediente para copiar su preparación:"}</h5>
      )}

      {groupPreparations !== undefined &&
        groupPreparations[currentGroup] !== "" && (
          <button
            className="p-4 m-3"
            style={{ fontSize: "12px" }}
            key={currentGroup + "PrepButton"}
            onClick={(ev) => {
              handleButtonClick(ev, currentGroup);
            }}
          >
            Preparación base del grupo
          </button>
        )}
      {ingredients.map((ingredient, index) => {
        return (
          <button
            className="p-4 m-3"
            style={{ fontSize: "12px" }}
            key={ingredient._id}
            onClick={(ev) => {
              handleButtonClick(ev, ingredient._id);
            }}
          >
            {ingredient._id !== undefined &&
              measures.find((alim) => alim._id === ingredient._id) !==
                undefined &&
              measures.find((alim) => alim._id === ingredient._id).name}
          </button>
        );
      })}
    </div>
  );
});

export const IngredientList = forwardRef((props, ref) => {
  const { measures, changeIngredientForm } = props;
  const [ingredients, setIngredients] = useState([]);

  useImperativeHandle(ref, () => ({
    addToList(ev) {
      let json = ingredientFormToJson(ev.currentTarget.form);
      let aliment =
        json._id === "" ? "" : measures.find((a) => a._id === json._id).name;
      if (aliment !== "") {
        console.log(json);
        handleAddIngredient(json, aliment);
      }
    },
    removeIngredientOfList(id) {
      removeIngredient(id);
    },
  }));

  function handleAddIngredient(json, name) {
    if (isOnTheList(json._id)) {
      removeIngredient(json._id);
    }

    const newIngredient = (
      <IngredientOnList
        key={json._id}
        name={name}
        json={json}
        changeIngredientForm={changeIngredientForm}
      />
    );
    setIngredients((i) => [...i, newIngredient]);
  }

  //True if an ingredient with that id is already saved
  function isOnTheList(id) {
    return ingredients.some((ingredient) => ingredient.key === id);
  }

  function removeIngredient(index) {
    setIngredients((ingredient) =>
      ingredient.filter((elem, id) => elem.key !== index)
    );
  }

  //Parses an ingredient form to JSON format
  function ingredientFormToJson(form) {
    const formData = new FormData(form);
    const json = {};

    formData.forEach((value, key) => {
      json[key] = value;
    });

    //(TEXT_AREA) Deleted \n to save plain text only
    json["ingredient_preparation"] = json["ingredient_preparation"].replace(
      /\n/g,
      " "
    );

    return json;
  }

  return (
    <div className="m-5 p-5">
      <div className="container-group col-sm-12" style={{ marginLeft: "-1em" }}>
        <h4 className="m-t-5">
          {ingredients.length === 0
            ? "Añade un ingrediente para verlo en el listado"
            : "Ingredientes añadidos:"}
        </h4>
        {ingredients.length === 0 ? "" : <hr className="m-t-10 m-b-1" />}
      </div>
      <div className="">{ingredients}</div>
    </div>
  );
});

//Single button on the list of saved ingredients
export const IngredientOnList = (props) => {
  const { name, json, changeIngredientForm } = props;

  return (
    <button
      className="p-5 m-3"
      type="button"
      onClick={() => changeIngredientForm(json)}
    >
      {name}
    </button>
  );
};

/**
 * COMPONENTES PARA COMPROBAR RECETA/NOMBRE DE LA RECETA
 */

export const CheckRecipeSection = forwardRef((props, ref) => {
  const {
    ingredients,
    ingredientGroups,
    measures,
    visibleNames,
    prepVar,
    generatedName,
    generatedPreparation,
  } = props;

  const [generatedNameText, setGeneratedNameText] = useState("");
  const [generatedPreparationText, setGeneratedPreparationText] = useState("");

  const glueExcepOptions = [
    { value: true, label: "De" },
    { value: false, label: "Con" },
  ];
  const [selectedGlueExcep, setSelectedGlueExcep] = useState(
    glueExcepOptions[0]
  );

  const [basePreparation, setBasePreparation] = useState("");
  const [recipeName, setRecipeName] = useState("");
  const [visibleIngredientList, setVisibleIngredientList] = useState([]);
  const [visibleGroups, setVisibleGroups] = useState([]);

  useEffect(() => {
    setGeneratedNameText(generatedName);
    setGeneratedPreparationText(generatedPreparation);
  }, [generatedName, generatedPreparation]);

  useEffect(() => {
    //update name_order fields
    setVisibleIngredientList(() => {
      var newList = getVisibleIngredients(ingredients);

      if (recipeName !== "") {
        var draggableValue = {
          id: "recipe_name",
          name: recipeName,
        };
        newList.push(draggableValue);
      }
      Object.entries(visibleNames).forEach(([groupName, name]) => {
        if (name !== "") {
          var draggableVisibleName = {
            id: convertVisibleName(groupName),
            name: name,
          };
          newList.push(draggableVisibleName);
        }
      });

      if (prepVar !== "") {
        var draggablePrepVar = { id: "prep_var", name: prepVar };
        newList.push(draggablePrepVar);
      }

      return newList;
    });

    //update preparation_order fields
    setVisibleGroups(() => {
      var newList = getVisibleGroups(ingredients);
      if (basePreparation !== "") {
        var draggableValue = {
          id: "Base_preparation",
          name: "Base_preparation",
        };
        newList.push(draggableValue);
      }
      return newList;
    });
  }, [ingredients, visibleNames, prepVar]);

  function getVisibleIngredients(ingredientList) {
    var newList = [];
    ingredientGroups.forEach((group) => {
      ingredientList[group].forEach((ingredient) => {
        if (ingredient._id !== undefined && ingredient.is_visible === "true") {
          newList.push({
            id: ingredient._id,
            name: measures.find((alim) => alim._id === ingredient._id).name,
          });
        }
      });
    });

    return newList;
  }

  function getVisibleGroups(ingredientList) {
    var newList = [];

    ingredientGroups.forEach((group) => {
      var breakIfGroupAdded = false;
      ingredientList[group].forEach((ingredient) => {
        if (breakIfGroupAdded) {
          return;
        }
        if (ingredient._id !== undefined && ingredient.is_visible === "true") {
          let preparationName = convertGroupName(group);
          newList.push({
            id: preparationName,
            name: preparationName,
          });
          breakIfGroupAdded = true;
        }
      });
    });
    return newList;
  }

  //FIXME: HARDCODED FOR NOW
  function convertGroupName(group) {
    switch (group) {
      case "CARB":
        return "Ch_preparation";
      case "PROT":
        return "Prot_preparation";
      case "VEGE":
        return "Vege_preparation";
      case "FATS":
        return "Fats_preparation";
      case "DAIR":
        return "Dairy_preparation";
      case "FRUT":
        return "Frut_preparation";
      case "SEAS":
        return "Seasoning_preparation";
      default:
        return "";
    }
  }

  //FIXME: HARDCODED FOR NOW
  function convertVisibleName(group) {
    switch (group) {
      case "CARB":
        return "ch_name";
      case "PROT":
        return "prot_name";
      case "VEGE":
        return "vege_name";
      case "FATS":
        return "fats_name";
      case "DAIR":
        return "dairy_name";
      case "FRUT":
        return "frut_name";
      case "SEAS":
        return "seasoning_name";
      default:
        return "";
    }
  }

  function handleChangeBasePreparation(ev) {
    var value = ev.target.value;
    setVisibleGroups((prevGroups) => {
      var draggableValue = {
        id: "Base_preparation",
        name: "Base_preparation",
      };
      var newGroups = prevGroups.filter(
        (item) => item.id !== "Base_preparation"
      );
      if (value !== "") {
        newGroups.push(draggableValue);
      }
      return newGroups;
    });
    setBasePreparation(value);
  }

  function handleChangeRecipeName(ev) {
    var value = ev.target.value;
    setVisibleIngredientList((prevIngredients) => {
      var draggableValue = {
        id: "recipe_name",
        name: value,
      };
      var newIngredients = prevIngredients.filter(
        (item) => item.id !== "recipe_name"
      );
      if (value !== "") {
        newIngredients.push(draggableValue);
      }
      return newIngredients;
    });
    setRecipeName(value);
  }

  return (
    <div style={{ marginLeft: "-1em" }}>
      <div className="container-group col-sm-12">
        <h3 className="m-t-10 m-b-0 ">{"Nombre y preparación"}</h3>
        <hr className="m-t-10" />
      </div>

      <div className="col-md-5">
        <div className="form-group">
          <label>Imagen</label>
          <ScrollableDragDropForm
            fileTypes={["JPG", "PNG", "GIF"]}
            compact={true}
          />
        </div>
      </div>

      <CreationFormInput
        id={""}
        name=""
        size={7 / 12}
        inputData={{
          visibleName: "Nombre generado",
          type: InputTypes.TEXT,
          placeholder: "Guarda la receta para generar un nombre...",
          readOnly: true,
        }}
        value={generatedNameText}
      />
      <CreationFormInput
        id={""}
        name=""
        size={7 / 12}
        inputData={{
          visibleName: "Preparación generada",
          type: InputTypes.TEXT,
          placeholder: "Guarda la receta para generar la preparación...",
          readOnly: true,
        }}
        value={generatedPreparationText}
      />
      <CreationFormInput
        id={"recipe_name"}
        name="recipe_name"
        size={4 / 12}
        inputData={{
          visibleName: "Nombre específico de la receta (recipe_name)",
          type: InputTypes.TEXT,
          placeholder: "Indica el nombre o parte del nombre...",
        }}
        value={recipeName}
        onChange={(ev) => handleChangeRecipeName(ev)}
      />
      <CreationFormInput
        id={"glue_excep_es"}
        name="glue_excep_es"
        size={3 / 12}
        value={selectedGlueExcep}
        onChange={(val) => setSelectedGlueExcep(val)}
        inputData={{
          visibleName: "Conjunción (glue_excep_es)",
          type: InputTypes.SELECT,
          optionsMapping: {
            source: OptionsSources.INLINE,
            options: glueExcepOptions,
          },
        }}
      />
      <CreationFormInput
        id={"base_preparation"}
        name="base_preparation"
        size={7 / 12}
        inputData={{
          visibleName: "Preparación base (base_preparation)",
          type: InputTypes.TEXT_AREA,
        }}
        value={basePreparation}
        onChange={(ev) => handleChangeBasePreparation(ev)}
      />

      <CreationFormInput
        id={"name_order"}
        name="name_order"
        size={1}
        items={visibleIngredientList}
        inputData={{
          visibleName: "Orden de los ingredientes (name_order)",
          placeholder: "Añade ingredientes para ordenarlos...",
          type: InputTypes.DRAG_AND_DROP_INGS,
        }}
      />
      <CreationFormInput
        id={"preparation_order"}
        name="preparation_order"
        size={1}
        items={visibleGroups}
        inputData={{
          visibleName: "Orden de preparación (preparation_order)",
          placeholder: "Añade ingredientes ordenar su preparación...",
          type: InputTypes.DRAG_AND_DROP_PREP,
        }}
      />
    </div>
  );
});
